import styled from "@emotion/styled";
import { ErrorMessage } from "@hookform/error-message";
import { Box, Button, IconButton, Stack } from "@mui/material";
import { EthiopianDatePicker } from "components/UI";
import { useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BsPlus, BsTrash } from "react-icons/bs";

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
`;

const HelperText = styled.span`
  font-size: 12px;
  color: ${({ theme, error }) =>
    error ? theme.palette.error.main : "inherit"};
`;

export const EducationBackgroundForm = ({
  register,
  errors,
  control,
  watch,
  setValue
}) => {
  const { t } = useTranslation();

  const {
    fields: educationBackgrounds,
    append: appendEducation,
    remove: removeEducation
  } = useFieldArray({
    control,
    name: "education_background"
  });

  return (
    <Stack spacing={2}>
      <Field>
        <Label>{t("HR.EducationBackground.CVDocument")}</Label>
        <Input
          type="file"
          accept=".pdf,.doc,.docx"
          {...register("employee_doc")}
        />
        <ErrorMessage
          errors={errors}
          name="employee_doc"
          render={({ message }) => <HelperText error>{message}</HelperText>}
        />
      </Field>

      {educationBackgrounds.map((education, index) => (
        <Box
          key={education.id}
          sx={{
            position: "relative",
            p: 2,
            border: "1px solid #e0e0e0",
            borderRadius: 1
          }}
        >
          <IconButton
            size="small"
            sx={{ position: "absolute", right: 8, top: 8 }}
            onClick={() => removeEducation(index)}
          >
            <BsTrash />
          </IconButton>
          <Stack spacing={2}>
            <Field>
              <Label>{t("HR.EducationBackground.AcademicStatus")}</Label>
              <Input
                {...register(`education_background.${index}.academic_status`)}
              />
              <ErrorMessage
                errors={errors}
                name={`education_background.${index}.academic_status`}
                render={({ message }) => (
                  <HelperText error>{message}</HelperText>
                )}
              />
            </Field>
            <Field>
              <Label>{t("HR.EducationBackground.Institution")}</Label>
              <Input
                {...register(`education_background.${index}.institution`)}
              />
              <ErrorMessage
                errors={errors}
                name={`education_background.${index}.institution`}
                render={({ message }) => (
                  <HelperText error>{message}</HelperText>
                )}
              />
            </Field>
            <Field>
              <Label>{t("HR.EducationBackground.FieldOfStudy")}</Label>
              <Input
                {...register(`education_background.${index}.field_of_study`)}
              />
              <ErrorMessage
                errors={errors}
                name={`education_background.${index}.field_of_study`}
                render={({ message }) => (
                  <HelperText error>{message}</HelperText>
                )}
              />
            </Field>
            <Field>
              <Label>{t("HR.EducationBackground.GraduationDate")}</Label>
              <EthiopianDatePicker
                value={watch(`education_background.${index}.graduate_date`)}
                onChange={e =>
                  setValue(
                    `work_experience.${index}.graduate_date`,
                    e.target.value
                  )
                }
              />
              <ErrorMessage
                errors={errors}
                name={`education_background.${index}.graduate_date`}
                render={({ message }) => (
                  <HelperText error>{message}</HelperText>
                )}
              />
            </Field>
          </Stack>
        </Box>
      ))}
      <Button
        type="button"
        onClick={() =>
          appendEducation({
            academic_status: "",
            institution: "",
            field_of_study: "",
            graduate_date: null
          })
        }
        startIcon={<BsPlus />}
        variant="outlined"
        fullWidth
      >
        {t("HR.EducationBackground.AddEductionBackground")}
      </Button>
    </Stack>
  );
};
